import * as THREE from 'three';
import { SeededRandom } from '../utils/random.js';
import { ANOMALIES, LAYOUT } from '../utils/constants.js';

/**
 * Deterministic zoning plan for a planet surface.
 *
 * Every scatter system used to sample the sphere independently, so rocks
 * intersected trees, ruins vanished inside forests, and the surface read as
 * uniform noise. The plan fixes that by acting as a shared occupancy map:
 *
 *   - It reserves a spawn clearing (and story-site clearings on the home
 *     planet) up front.
 *   - It picks evenly-spread landmark anchors for anomalies and reserves a
 *     barren clearing around each, so ruins read as deliberate places.
 *   - Generators reserve footprints for their clusters ("rock garden",
 *     "crystal field", "thermal basin") and query `isFree` before placing,
 *     so zones never collide and open corridors survive between them.
 *
 * All reservations are angular caps on the unit sphere; world radii are
 * converted via the planet radius. Construction is seeded, so two clients
 * given the same planetData produce identical plans.
 */
export class SurfacePlan {
  /**
   * @param {object} planetData   – planet config (seed, type, size …)
   * @param {number} planetRadius – SURFACE_RADIUS × size
   * @param {{ reserveStorySites?: boolean }} opts
   */
  constructor(planetData, planetRadius, { reserveStorySites = false } = {}) {
    this.planetRadius = planetRadius;
    /** @type {{ dir: THREE.Vector3, angRadius: number, tag: string }[]} */
    this.reservations = [];
    this.rng = new SeededRandom((planetData.seed ?? 1) + 424242);

    // Spawn / landing clearing — the ship and player always start at +Y.
    this.reserve(new THREE.Vector3(0, 1, 0), LAYOUT.SPAWN_CLEARING, 'clearing');

    if (reserveStorySites) {
      // Must match the authored anchors in StorySites.build().
      this.reserve(new THREE.Vector3(0.28, 1, 0.1).normalize(), LAYOUT.STORY_CLEARING, 'clearing');
      this.reserve(new THREE.Vector3(-0.55, 1, -0.75).normalize(), LAYOUT.STORY_CLEARING, 'clearing');
    }

    // Landmark anchors — these become the anomaly sites. Spread them as far
    // apart as possible so each one is a destination in a different direction.
    let landmarkCount = 0;
    if (planetData.type !== 'gas') {
      landmarkCount = ANOMALIES.MIN_PER_PLANET +
        Math.floor(this.rng.next() * (ANOMALIES.MAX_PER_PLANET - ANOMALIES.MIN_PER_PLANET + 1));
    }
    /** @type {THREE.Vector3[]} */
    this.landmarkDirs = this.pickSpreadDirs(landmarkCount, LAYOUT.SITE_CLEARING, 'clearing');
  }

  _angRadius(worldRadius) {
    return worldRadius / this.planetRadius;
  }

  /** Uniform random direction on the unit sphere. */
  randomDir(rng = this.rng) {
    const u = rng.next();
    const v = rng.next();
    const theta = Math.acos(2 * u - 1);
    const phi = 2 * Math.PI * v;
    return new THREE.Vector3(
      Math.sin(theta) * Math.cos(phi),
      Math.cos(theta),
      Math.sin(theta) * Math.sin(phi),
    );
  }

  /** Mark a cap of the sphere as occupied. */
  reserve(dir, worldRadius, tag) {
    this.reservations.push({
      dir: dir.clone().normalize(),
      angRadius: this._angRadius(worldRadius),
      tag,
    });
  }

  /**
   * True if a cap of `worldRadius` around `dir` overlaps no reservation.
   * @param {string[]|null} ignoreTags – reservation tags to skip
   */
  isFree(dir, worldRadius, ignoreTags = null) {
    const r = this._angRadius(worldRadius);
    for (const res of this.reservations) {
      if (ignoreTags && ignoreTags.includes(res.tag)) continue;
      if (dir.angleTo(res.dir) < res.angRadius + r) return false;
    }
    return true;
  }

  /** Like isFree, but only tests reservations with the given tag. */
  clearOfTag(dir, worldRadius, tag) {
    const r = this._angRadius(worldRadius);
    for (const res of this.reservations) {
      if (res.tag !== tag) continue;
      if (dir.angleTo(res.dir) < res.angRadius + r) return false;
    }
    return true;
  }

  /** Reserve if free; returns whether the reservation was made. */
  tryReserve(dir, worldRadius, tag) {
    if (!this.isFree(dir, worldRadius)) return false;
    this.reserve(dir, worldRadius, tag);
    return true;
  }

  /**
   * Pick `count` directions spread as far as possible from every existing
   * reservation (best-candidate sampling), reserving each as it is chosen.
   * Candidates that would still land inside occupied space are dropped, so
   * on crowded (small) planets fewer anchors come back rather than overlaps.
   * @returns {THREE.Vector3[]}
   */
  pickSpreadDirs(count, worldRadius, tag, rng = this.rng) {
    const dirs = [];
    const ownR = this._angRadius(worldRadius);
    for (let i = 0; i < count; i++) {
      let best = null;
      let bestScore = -Infinity;
      for (let c = 0; c < 24; c++) {
        const cand = this.randomDir(rng);
        let minGap = Infinity;
        for (const res of this.reservations) {
          minGap = Math.min(minGap, cand.angleTo(res.dir) - res.angRadius);
        }
        if (minGap > bestScore) {
          bestScore = minGap;
          best = cand;
        }
      }
      if (!best || bestScore < ownR * 0.5) continue;
      this.reserve(best, worldRadius, tag);
      dirs.push(best);
    }
    return dirs;
  }

  /** Random direction within `worldSpread` (surface distance) of `anchor`. */
  sampleNear(anchor, worldSpread, rng = this.rng) {
    const ang = Math.sqrt(rng.next()) * this._angRadius(worldSpread);
    return this._offsetByAngle(anchor, ang, rng.next() * Math.PI * 2);
  }

  /** Direction exactly `worldDist` (surface distance) from `anchor`, random azimuth. */
  offsetFrom(anchor, worldDist, rng = this.rng) {
    return this._offsetByAngle(anchor, this._angRadius(worldDist), rng.next() * Math.PI * 2);
  }

  _offsetByAngle(anchor, ang, azimuth) {
    const n = anchor.clone().normalize();
    // Build a tangent basis around the anchor.
    const helper = Math.abs(n.y) < 0.99 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0);
    const t1 = new THREE.Vector3().crossVectors(helper, n).normalize();
    const t2 = new THREE.Vector3().crossVectors(n, t1);
    return n.multiplyScalar(Math.cos(ang))
      .addScaledVector(t1, Math.sin(ang) * Math.cos(azimuth))
      .addScaledVector(t2, Math.sin(ang) * Math.sin(azimuth))
      .normalize();
  }
}
